/**
 * onboardingResume.ts
 *
 * Derives the furthest reachable wizard step from actual entity counts
 * (properties_count, customers_count, leases_count, invoices_count) so the
 * "Resume setup" badge and the DashboardChecklist links always land the user
 * on the correct step — even when onboarding_step was never written because
 * the user browsed the wizard without hitting Save & Continue.
 *
 * Count → step mapping (mirrors REQUIRES_* sets in OnboardingWizardPage):
 *   Step 1 — Property info     (no upstream prereq — always reachable)
 *   Step 2 — Pricing           (requires properties_count >= 1)
 *   Step 3 — Rooms/Units       (requires properties_count >= 1)
 *   Step 4 — Customer          (requires properties_count >= 1)
 *   Step 5 — Lease             (requires customers_count >= 1)
 *   Step 6 — Meter readings    (requires leases_count >= 1)
 *   Step 7 — Invoice           (requires leases_count >= 1)
 *   Step 8 — Payment           (requires invoices_count >= 1)
 *
 * INVARIANT (C1): onboarding_step is written ONLY by advanceToStep (Save &
 * Continue). This helper never writes it — it only reads entity counts.
 */

import type { AccountSettings } from '@/types'

/**
 * Returns the furthest wizard step the user can land on given what actually
 * exists in the database (derived from entity counts on accountSettings).
 * Falls back to step 1 when nothing has been created yet.
 */
export function deriveResumeStep(accountSettings: AccountSettings | undefined): number {
  if (!accountSettings) return 1

  const propertiesCount = accountSettings.properties_count ?? 0
  const customersCount = accountSettings.customers_count ?? 0
  const leasesCount = accountSettings.leases_count ?? 0
  const invoicesCount = accountSettings.invoices_count ?? 0

  // Walk backwards from the highest step to find the furthest reachable one.
  if (invoicesCount >= 1) return 8    // payment step reachable
  if (leasesCount >= 1) return 7      // invoice step reachable (lease exists)
  if (customersCount >= 1) return 5   // lease step reachable (customer exists)
  if (propertiesCount >= 1) return 4  // customer step reachable (property exists)
  return 1                            // no entities yet — start at property step
}

/**
 * Builds a URLSearchParams for the resume link that carries:
 *   - step  : derived from entity counts (ignores onboarding_step)
 *   - property, customer, lease, invoice : entity IDs stored in settings
 *     (written only by advanceToStep / Save & Continue)
 *
 * The entity IDs prevent the wizard's missing-upstream guard from cascading
 * the user backwards (e.g. step 5 with no customer param → bounced to step 4).
 */
export function buildResumeParams(accountSettings: AccountSettings | undefined): URLSearchParams {
  const step = deriveResumeStep(accountSettings)
  const settings = accountSettings?.settings

  const params = new URLSearchParams({ step: String(step) })
  if (settings?.onboarding_property_id) params.set('property', String(settings.onboarding_property_id))
  if (settings?.onboarding_customer_id) params.set('customer', String(settings.onboarding_customer_id))
  if (settings?.onboarding_lease_id) params.set('lease', String(settings.onboarding_lease_id))
  if (settings?.onboarding_invoice_id) params.set('invoice', String(settings.onboarding_invoice_id))
  if (settings?.onboarding_data_import_id) params.set('import', String(settings.onboarding_data_import_id))

  return params
}

/**
 * Builds a URLSearchParams for a specific step link (e.g. DashboardChecklist
 * rows). For steps that require upstream entities, the entity IDs are included
 * so the wizard's missing-upstream guard doesn't cascade the user backwards.
 *
 *   Steps 2-4 require property → include property ID
 *   Steps 5   require customer → include property + customer IDs
 *   Steps 6-7 require lease   → include property + customer + lease IDs
 *   Step  8   requires invoice → include all four IDs
 */
export function buildStepParams(
  step: number,
  accountSettings: AccountSettings | undefined,
): URLSearchParams {
  const settings = accountSettings?.settings
  const params = new URLSearchParams({ step: String(step) })

  // Steps 2-8 require a property
  if (step >= 2 && settings?.onboarding_property_id) {
    params.set('property', String(settings.onboarding_property_id))
  }
  // Steps 5-8 require a customer
  if (step >= 5 && settings?.onboarding_customer_id) {
    params.set('customer', String(settings.onboarding_customer_id))
  }
  // Steps 6-8 require a lease
  if (step >= 6 && settings?.onboarding_lease_id) {
    params.set('lease', String(settings.onboarding_lease_id))
  }
  // Step 8 requires an invoice
  if (step >= 8 && settings?.onboarding_invoice_id) {
    params.set('invoice', String(settings.onboarding_invoice_id))
  }

  return params
}
